import React, { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  Alert,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  InputLabel,
  Radio,
  RadioGroup,
  Stack,
  styled,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { FaAngleRight } from "@react-icons/all-files/fa/FaAngleRight";
import { useDispatch, useSelector } from "react-redux";
import UserDetails from "@shared/userDetails";
import StickyActionContainer from "@shared/stickyActionContainer";
import Loader from "@shared/loader";
import MobileDropdown from "@shared/mobileDropdown";
import MessageViewer from "@shared/messageViewer/";
import { MdOutlinePhoneSVG } from "@shared/MdOutlinePhoneSVG";
import { MdOutlinePersonSVG } from "@shared/MdOutlinePersonSVG";
import {
  setError,
  setPanStatus,
  setSuccessValue,
  ValidatePAN,
} from "../../../../store/slices/validatePAN";
import { IBankDetailsForm } from "../bankDetails";
import { IAllData } from "../KYCsteps";
import {
  KYCConstants,
  KYCRegxConstants,
} from "../../../../constants/application";
import { ControlsContainer, RedAsterisk } from "../../../../styles/controls";
import { ESTABLISHMENT_OPTIONS } from "../../../../constants/establishmentTypeOptions";
import PanDiffAcc from "./panDiffAcc/panDiffAcc";
import PanInfoContainer from "./panInfo";
import PanUnavailableNote from "./panUnavailableNote";
import { PAN_STATUS_VARIANTS } from "constants/panStatusVariants";
import { setBankStatus } from "store/slices/validateBank";
import { BANK_STATUS_VARIANTS } from "constants/bankStatusVariants";
import { AppSpecificName } from "constants/headerImage";

export interface IBasicDetailsForm {
  establishmentType: string | null;
  mobileNumber: string;
  panNumber: string;
  tdsStatus: boolean;
}

export const BasicDetailsFormDefaultValues: IBasicDetailsForm = {
  establishmentType: "",
  mobileNumber: "",
  panNumber: "",
  tdsStatus: false,
};

const CheckboxContainer = styled("div")`
  display: flex;
  align-items: flex-start;
  margin-top: -16px;
`;

interface IBasicDetails {
  nextStep: (data: IBasicDetailsForm | IBankDetailsForm) => void;
  formData: IAllData;
}
const appName = localStorage.getItem('appname');
// First step of KYC process
function BasicDetails({ nextStep, formData }: IBasicDetails) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const panValidation = useSelector((state: any) => state.pan);
  const info = useSelector((state: any) => state.info.allData);
  const methods = useForm<IBasicDetailsForm>({
    mode: "all",
    defaultValues: BasicDetailsFormDefaultValues,
  });
  const {
    control,
    getValues,
    watch,
    setValue,
    clearErrors,
    unregister,
    reset,
    formState: { isValid, isDirty },
  } = methods;
  const taxDetailsShow = watch("tdsStatus");

  useEffect(() => {
    // Setting default value of form ( also helps in edit details in 3rd step of KYC process )
    const _formData: IBasicDetailsForm = {
      ...BasicDetailsFormDefaultValues,
      panNumber: formData.panNumber,
      establishmentType: formData.establishmentType,
      mobileNumber: info?.mobileNumber,
      tdsStatus: formData.tdsStatus,
    };
    reset(_formData);
  }, [formData]);

  useEffect(() => {
    // Once pan verification is done and further changes to form is made,
    // Making pan valid as false
    if (isDirty) {
      if (
        panValidation.panStatus === PAN_STATUS_VARIANTS.PAN_UNAVAILABLE_CHECKED
      ) {
        dispatch(
          setPanStatus({
            value: PAN_STATUS_VARIANTS.PAN_UNAVAILABLE_UNCHECKED,
          }) as any
        );
      } else if (
        panValidation.panStatus ===
        PAN_STATUS_VARIANTS.PAN_UNAVAILABLE_UNCHECKED
      ) {
        // do nothing
      } else {
        dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PENDING }) as any);
      }
    }
  }, [isDirty]);

  useEffect(() => {
    // Once the pan details is verified , establish form values as default values,
    // so that any further changes to form should make form dirty( ready for revalidation of pan )
    if (panValidation.success) {
      if (
        panValidation?.panStatus === PAN_STATUS_VARIANTS.PAN_UNAVAILABLE_CHECKED
      ) {
        nextStep(getValues());
        dispatch(setSuccessValue({ value: false }) as any);
      } else {
        const _formData = getValues();
        reset(_formData);
      }
    }
  }, [panValidation.success]);

  const actionHandler = () => {
    // If basic step is done with or without pan go to next step or else dispatch event to complete basic step
    if (
      panValidation.panStatus === PAN_STATUS_VARIANTS.PAN_VERIFIED ||
      panValidation.panStatus === PAN_STATUS_VARIANTS.PAN_UNAVAILABLE_CHECKED ||
      panValidation.panStatus === PAN_STATUS_VARIANTS.PAN_INACTIVE ||
      panValidation.panStatus === PAN_STATUS_VARIANTS.PAN_INOPERATIVE
    ) {
      nextStep(getValues());
    } else {
      const formData = getValues();
      const _apiData = {
        mobileNumber: info?.mobileNumber,
        customerId: info?.customerId,
        panNumber: formData.panNumber,
        establishmentType: formData.establishmentType,
        customerSource:''
      };
      dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.PENDING }));
      dispatch(setError({ value: "" }) as any);
      dispatch(ValidatePAN({ data: _apiData }) as any);
    }
  };

  const validatePANRule = (value: string) => {
    const panRegex = KYCRegxConstants.panRegx;
    if (!panRegex.test(value.trim())) {
      return t("invalid_pan", {
        defaultValue: "Invalid PAN format. Example: ADCDE1234F",
      });
    }
  };
  const panLinkageAction = (actionType: "CONTINUE" | "RESET") => {
    if (actionType === "CONTINUE") {
      dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PAN_VERIFIED }));
    } else {
      const _formData = getValues();
      reset({ ..._formData, panNumber: "" });
      dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PENDING }));
    }
  };

  return (
    <Stack direction={"column"} flexGrow={1}>
      <UserDetails />
      {panValidation?.error && (
        <Alert severity="error" sx={{ ml: 2.25, mb: 2.25, mr: 2.25 }}>
          {panValidation?.error}
        </Alert>
      )}
      <FormProvider {...methods}>
        <form autoComplete="off">
          <ControlsContainer>
            <Controller
              control={control}
              name="mobileNumber"
              rules={{
                required: t("required_text", {
                  defaultValue: "This is required field",
                }),
              }}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <InputLabel>
                    {t("mobile_number", {
                      defaultValue: "Mobile Number",
                    })}
                    <RedAsterisk>&thinsp;*</RedAsterisk>
                  </InputLabel>
                  <TextField
                    {...field}
                    fullWidth={true}
                    disabled
                    required
                    error={error !== undefined}
                    helperText={
                      <>
                        {error && (
                          <MessageViewer
                            status="error"
                            details={error?.message}
                          />
                        )}
                      </>
                    }
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <MdOutlinePhoneSVG />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </div>
              )}
            />
            <MobileDropdown
              name={"establishmentType"}
              label={t("type_of_establishment", {
                defaultValue: "Type of Establishment",
              })}
              title={t("select_establishment", {
                defaultValue: "Select Type of Establishment",
              })}
              placeholder={t("select", { defaultValue: "Select..." })}
              options={ESTABLISHMENT_OPTIONS}
              rules={{
                required: t("required_text", {
                  defaultValue: "This is required field",
                }),
              }}
            />
            {appName !== AppSpecificName.SEMINIS && <FormControl component="fieldset" margin="normal">
              <FormLabel
                sx={{ "&.Mui-focused": { color: "#666666" } }}
                component="legend"
              >
                {t("do_you_have_pan", {
                  defaultValue: "Do you have a pan number?",
                })}
              </FormLabel>
              <Controller
                name="tdsStatus"
                control={control}
                render={({ field }) => {
                  const showOnlyYes = appName === AppSpecificName.SEMINIS;
                  const selectedValue = showOnlyYes ? "false" : field.value === true ? "true" : "false";

                  return (
                    <RadioGroup
                      {...field}
                      row
                      value={selectedValue}
                      onChange={(e) => {
                        if (e.target.value === "true") {
                          clearErrors("panNumber");
                          unregister("panNumber");
                          setValue("panNumber", "");
                          dispatch(
                            setPanStatus({
                              value: PAN_STATUS_VARIANTS.PAN_UNAVAILABLE_UNCHECKED,
                            }) as any
                          );
                        } else {
                          dispatch(
                            setPanStatus({
                              value: PAN_STATUS_VARIANTS.PENDING,
                            }) as any
                          );
                        }
                        field.onChange(e.target.value === "true");
                      }}
                    >
                      <FormControlLabel
                        value={"false"}
                        control={<Radio />}
                        label={t("yes", { defaultValue: "Yes" })}
                      />
                      {!showOnlyYes && (
                        <FormControlLabel
                          value={"true"}
                          control={<Radio />}
                          label={t("no", { defaultValue: "No" })}
                        />
                      )}
                    </RadioGroup>
                  );
                }}
              />
            </FormControl>}
            

            {/* <Controller
              name="tdsStatus"
              control={control}
              render={({ field }) => (
                <div>
                  <CheckboxContainer>
                    <Checkbox
                      {...field}
                      size="large"
                      checked={field.value}
                      onChange={(e) => {
                        if (e.target.checked) {
                          clearErrors("panNumber");
                          unregister("panNumber");
                          setValue("panNumber", "");
                          dispatch(
                            setPanStatus({
                              value:
                                PAN_STATUS_VARIANTS.PAN_UNAVAILABLE_UNCHECKED,
                            }) as any
                          );
                        } else {
                          dispatch(
                            setPanStatus({
                              value: PAN_STATUS_VARIANTS.PENDING,
                            }) as any
                          );
                        }
                        field.onChange(e.target.checked);
                      }}
                    />
                    <Typography
                      color="#666666"
                      component={"div"}
                      sx={{ mt: 1.5 }}
                    >
                      {t("pan_unavailable", {
                        defaultValue: "I do not have a pan number",
                      })}
                    </Typography>
                  </CheckboxContainer>
                </div>
              )}
            /> */}
            <Controller
              key={String(taxDetailsShow)}
              control={control}
              name="panNumber"
              rules={
                !taxDetailsShow
                  ? {
                    required: t("required_text", {
                      defaultValue: "This is required field",
                    }),
                    validate: validatePANRule,
                  }
                  : undefined
              }
              render={({ field, fieldState: { error } }) => (
                <div>
                  <InputLabel>
                    {t("pan", {
                      defaultValue: "Pan Number",
                    })}
                    <RedAsterisk>&thinsp;*</RedAsterisk>
                  </InputLabel>
                  <TextField
                    {...field}
                    onChange={(e) => {
                      // Common practice to keep PAN in upper case
                      // don't remove if condition , as character max length is not working on in app browser, this is work around
                      if (e.target.value.length <= KYCConstants.panCharLimit)
                        field.onChange(e.target.value.toUpperCase());
                    }}
                    error={error !== undefined}
                    fullWidth={true}
                    helperText={
                      <>
                        {error && (
                          <MessageViewer
                            status="error"
                            details={error?.message}
                          />
                        )}
                      </>
                    }
                    required
                    disabled={taxDetailsShow}
                    slotProps={{
                      htmlInput: { maxLength: KYCConstants.panCharLimit },
                      input: {
                        placeholder: t("enter_pan", {
                          defaultValue: "Enter your Pan number",
                        }),
                        startAdornment: (
                          <InputAdornment position="start">
                            <MdOutlinePersonSVG />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  <PanInfoContainer />
                  <PanUnavailableNote />
                </div>
              )}
            />
          </ControlsContainer>
        </form>
      </FormProvider>
      <StickyActionContainer>
        <Button
          onClick={actionHandler}
          size="large"
          fullWidth
          variant={
            panValidation.panStatus === PAN_STATUS_VARIANTS.PENDING
              ? "outlined"
              : "contained"
          }
          disabled={!isValid || panValidation.loading}
          endIcon={
            panValidation.panStatus !== PAN_STATUS_VARIANTS.PENDING && (
              <FaAngleRight size={15} />
            )
          }
        >
          {panValidation.panStatus === PAN_STATUS_VARIANTS.PENDING
            ? t("verify_pan", { defaultValue: "Verify Pan Details" })
            : t("next", { defaultValue: "Next" })}
        </Button>
      </StickyActionContainer>
      {panValidation.loading && <Loader />}

      {panValidation.panStatus === "PAN_ASSOCIATION" && (
        <PanDiffAcc panAction={panLinkageAction} />
      )}
    </Stack>
  );
}

export default BasicDetails;
